import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const NewSupplierModal = ({ isOpen, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    cnpj: '',
    companyType: '',
    contactPerson: '',
    bankInfo: '',
    notes: ''
  });

  const [errors, setErrors] = useState({});

  if (!isOpen) return null;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors?.[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  const validateCNPJ = (cnpj) => {
    const cleanCNPJ = cnpj?.replace(/[^\d]/g, '');
    return cleanCNPJ?.length === 14;
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData?.name?.trim()) {
      newErrors.name = 'Nome da empresa é obrigatório';
    }

    if (formData?.email?.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(formData?.email)) {
      newErrors.email = 'Email inválido';
    }

    if (formData?.cnpj && !validateCNPJ(formData?.cnpj)) {
      newErrors.cnpj = 'CNPJ inválido (deve ter 14 dígitos)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors)?.length === 0;
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    
    if (validateForm()) {
      const newSupplier = {
        id: Date.now(),
        name: formData?.name,
        email: formData?.email || '-',
        phone: formData?.phone || '-',
        address: formData?.address || '-',
        cnpj: formData?.cnpj || '-',
        companyType: formData?.companyType || '-',
        contactPerson: formData?.contactPerson || '-',
        bankInfo: formData?.bankInfo || '-',
        status: 'active',
        statusLabel: 'Ativo',
        notes: formData?.notes ? [{
          id: 1,
          content: formData?.notes,
          author: 'Sistema',
          date: new Date()?.toISOString()?.split('T')?.[0]
        }] : [],
        createdAt: new Date()?.toISOString()
      };

      onSave(newSupplier);
      handleClose();
    }
  };

  const handleClose = () => {
    setFormData({
      name: '',
      email: '',
      phone: '',
      address: '',
      cnpj: '',
      companyType: '',
      contactPerson: '',
      bankInfo: '',
      notes: ''
    });
    setErrors({});
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div 
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      <div className="relative bg-card rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
        <div className="sticky top-0 bg-card border-b border-border p-4 flex items-center justify-between z-10">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="Truck" size={24} />
            Cadastrar Novo Fornecedor
          </h2>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-muted rounded-md transition-colors"
          >
            <Icon name="X" size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 overflow-y-auto max-h-[calc(90vh-140px)]">
          <div className="space-y-6">
            {/* Informações Básicas da Empresa */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Building" size={16} />
                Informações da Empresa
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Nome da Empresa *
                  </label>
                  <Input
                    type="text"
                    value={formData?.name}
                    onChange={(e) => handleChange('name', e?.target?.value)}
                    placeholder="Digite o nome da empresa"
                    className={errors?.name ? 'border-error' : ''}
                  />
                  {errors?.name && (
                    <p className="text-error text-xs mt-1">{errors?.name}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    CNPJ
                  </label>
                  <Input
                    type="text"
                    value={formData?.cnpj}
                    onChange={(e) => handleChange('cnpj', e?.target?.value)}
                    placeholder="00.000.000/0000-00"
                    maxLength={18}
                    className={errors?.cnpj ? 'border-error' : ''}
                  />
                  {errors?.cnpj && (
                    <p className="text-error text-xs mt-1">{errors?.cnpj}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Tipo de Empresa
                  </label>
                  <Input
                    type="text"
                    value={formData?.companyType}
                    onChange={(e) => handleChange('companyType', e?.target?.value)}
                    placeholder="Ex: Agência, Hotel, Transporte"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Endereço
                  </label>
                  <Input
                    type="text"
                    value={formData?.address}
                    onChange={(e) => handleChange('address', e?.target?.value)}
                    placeholder="Rua, número, bairro, cidade, estado"
                  />
                </div>
              </div>
            </div>

            {/* Informações de Contato */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="Phone" size={16} />
                Informações de Contato
              </h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Email
                  </label>
                  <Input
                    type="email"
                    value={formData?.email}
                    onChange={(e) => handleChange('email', e?.target?.value)}
                    placeholder="fornecedor@empresa.com"
                    className={errors?.email ? 'border-error' : ''}
                  />
                  {errors?.email && (
                    <p className="text-error text-xs mt-1">{errors?.email}</p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Telefone
                  </label>
                  <Input
                    type="tel"
                    value={formData?.phone}
                    onChange={(e) => handleChange('phone', e?.target?.value)}
                    placeholder="(00) 00000-0000"
                  />
                </div>

                <div className="col-span-2">
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Pessoa de Contato
                  </label>
                  <Input
                    type="text"
                    value={formData?.contactPerson}
                    onChange={(e) => handleChange('contactPerson', e?.target?.value)}
                    placeholder="Nome da pessoa responsável"
                  />
                </div>
              </div>
            </div>

            {/* Informações Financeiras */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="CreditCard" size={16} />
                Informações Bancárias
              </h3>
              <div className="grid grid-cols-1 gap-4">
                <div>
                  <label className="block text-sm font-medium text-foreground mb-2">
                    Dados Bancários
                  </label>
                  <Input
                    type="text"
                    value={formData?.bankInfo}
                    onChange={(e) => handleChange('bankInfo', e?.target?.value)}
                    placeholder="Banco, Agência, Conta"
                  />
                </div>
              </div>
            </div>

            {/* Observações */}
            <div>
              <h3 className="text-sm font-semibold text-foreground mb-4 flex items-center gap-2">
                <Icon name="MessageSquare" size={16} />
                Observações
              </h3>
              <textarea
                value={formData?.notes}
                onChange={(e) => handleChange('notes', e?.target?.value)}
                placeholder="Informações adicionais sobre o fornecedor..."
                rows={4}
                className="w-full px-3 py-2 text-sm border border-border rounded-md bg-background text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
              />
            </div>
          </div>
        </form>

        <div className="sticky bottom-0 bg-card border-t border-border p-4 flex items-center justify-end gap-3">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="default"
            iconName="Check"
            iconPosition="left"
            onClick={handleSubmit}
          >
            Cadastrar Fornecedor
          </Button>
        </div>
      </div>
    </div>
  );
};

export default NewSupplierModal;